import { useState, useEffect, useCallback, useRef } from "react";
import { AdminCard, AdminCardHead, LoadFailedBanner } from "components/admin/ui";
import { adminGet, adminPut, adminPost, adminPostForm } from "lib/adminClient";
import { mediaUrl } from "lib/media";

const PROFILE_FIELDS = [
  ["name", "Institution Name"],
  ["college", "College Wing"],
  ["eiin", "EIIN"],
  ["established", "Established"],
  ["phone", "Phone (School)"],
  ["phoneCollege", "Phone (College)"],
  ["email", "Email"],
  ["website", "Website"],
  ["address", "Address"],
  ["officeHours", "Office Hours"],
];

const pwInput =
  "w-full px-3.5 py-2.5 border-[1.5px] border-line rounded-lg font-sans text-sm outline-none focus:border-green";

export default function SettingsPage() {
  const [logo, setLogo] = useState(null);
  const [profile, setProfile] = useState({});
  const [socials, setSocials] = useState([]);
  const [footerProgs, setFooterProgs] = useState([]);
  const [connectedUrl, setConnectedUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("site");
  const fileRef = useRef();

  // Change-password state
  const [cp, setCp] = useState({ current: "", next: "", confirm: "" });
  const [cpBusy, setCpBusy] = useState(false);
  const [cpStatus, setCpStatus] = useState("");
  const [cpError, setCpError] = useState("");

  const [loadFailed, setLoadFailed] = useState(false);


  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    setError("");
    try {
      const data = (await adminGet("/admin/content/settings")) || {};
      setLogo(data.logo || null);
      setProfile(data.profile || {});
      setSocials(data.socials || []);
      setFooterProgs(data.footerProgrammes || []);
      setConnectedUrl(data.connectedUrl || "");
    } catch (e) {
      setLoadFailed(true);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const form = new FormData();
      form.append("image", file);
      const { url } = await adminPostForm("/admin/media", form);
      setLogo(url);
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (loadFailed) {
      setError("Content didn't load, so saving now would erase it. Reload the page first.");
      return;
    }
    setStatus("");
    setError("");
    try {
      await adminPut("/admin/content/settings", {
        logo,
        profile,
        connectedUrl: connectedUrl.trim(),
        socials: socials.filter((s) => (s.platform || "").trim() && (s.url || "").trim()),
        footerProgrammes: footerProgs.filter((l) => (l.label || "").trim()),
      });
      setStatus("Saved.");
      setTimeout(() => setStatus(""), 2500);
    } catch (e) {
      setError(e.message);
    }
  };

  const changePassword = async () => {
    setCpError("");
    setCpStatus("");
    if (cp.next.length < 8) return setCpError("New password must be at least 8 characters.");
    if (cp.next !== cp.confirm) return setCpError("New passwords do not match.");
    setCpBusy(true);
    try {
      await adminPost("/auth/change-password", { currentPassword: cp.current, newPassword: cp.next });
      setCp({ current: "", next: "", confirm: "" });
      setCpStatus("Password changed.");
      setTimeout(() => setCpStatus(""), 3000);
    } catch (e) {
      setCpError(e.message);
    } finally {
      setCpBusy(false);
    }
  };

  if (loading) return <div className="px-1 py-8 text-muted text-sm">Loading…</div>;

  return (
    <div className="animate-fadeIn max-w-[720px]">
      {/* Tabs */}
      <div className="flex gap-2 mb-5">
        {[
          ["site", "Site Settings"],
          ["password", "Password"],
        ].map(([k, label]) => {
          const on = tab === k;
          return (
            <button
              key={k}
              onClick={() => setTab(k)}
              className="px-5 py-2 rounded-lg border-[1.5px] font-sans font-semibold text-sm cursor-pointer transition-colors"
              style={{
                borderColor: on ? "#005C40" : "#e6e9ec",
                background: on ? "#005C40" : "#fff",
                color: on ? "#fff" : "#5c5c5c",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {loadFailed && <LoadFailedBanner onRetry={load} />}


      {error && (
        <div className="text-[13px] text-red bg-red/[0.08] rounded-lg px-4 py-2.5 mb-4">{error}</div>
      )}

      {tab === "site" && (
        <>
      <AdminCard className="mb-[18px]">
        <AdminCardHead title="School Logo" />
        <div className="p-[22px] flex items-center gap-5">
          <div className="w-[72px] h-[72px] rounded-full bg-green flex items-center justify-center overflow-hidden shrink-0">
            {logo ? (
              <img src={mediaUrl(logo)} alt="Logo" className="w-full h-full object-cover" />
            ) : (
              <span className="text-white/70 text-[11px] text-center px-1">No logo</span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="bg-green text-white border-none rounded-lg px-4 py-2 font-sans font-semibold text-[13px] cursor-pointer hover:opacity-90 disabled:opacity-60"
              >
                {uploading ? "Uploading…" : logo ? "Change logo" : "Upload logo"}
              </button>
              {logo && (
                <button
                  onClick={() => setLogo(null)}
                  className="bg-[#fdeaea] border-none rounded-lg px-4 py-2 font-sans font-semibold text-[13px] text-[#c44] cursor-pointer"
                >
                  Remove
                </button>
              )}
            </div>
            <span className="text-[12px] text-muted">Square image works best (PNG/JPG). Shown in the site header.</span>
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
        </div>
      </AdminCard>

      <AdminCard className="mb-[18px]">
        <AdminCardHead title="School Profile" />
        <div className="p-[22px] grid grid-cols-1 sm:grid-cols-2 gap-4">
          {PROFILE_FIELDS.map(([key, label]) => (
            <div key={key}>
              <label className="text-[12.5px] font-semibold text-muted block mb-1.5">{label}</label>
              <input
                value={profile[key] ?? ""}
                onChange={(e) => setProfile((p) => ({ ...p, [key]: e.target.value }))}
                className="w-full px-3.5 py-2.5 border-[1.5px] border-line rounded-lg font-sans text-sm outline-none focus:border-green"
              />
            </div>
          ))}
        </div>
      </AdminCard>

      <AdminCard className="mb-[18px]">
        <AdminCardHead title="ConnectEd Portal" />
        <div className="p-[22px]">
          <label className="text-[12.5px] font-semibold text-muted block mb-1.5">Login URL</label>
          <input
            value={connectedUrl}
            onChange={(e) => setConnectedUrl(e.target.value)}
            placeholder="https://portal.connected.example/login"
            className="w-full px-3.5 py-2.5 border-[1.5px] border-line rounded-lg font-sans text-sm outline-none focus:border-green"
          />
          <span className="text-[12px] text-muted mt-1.5 block">
            Where the homepage “Login to ConnectEd” button points.
          </span>
        </div>
      </AdminCard>

      <AdminCard className="mb-[18px]">
        <AdminCardHead
          title="Footer — Programmes links"
          action={
            <button
              onClick={() => setFooterProgs([...footerProgs, { label: "", href: "" }])}
              className="bg-green text-white border-none rounded-lg px-3.5 py-1.5 font-sans font-semibold text-xs cursor-pointer hover:opacity-90"
            >
              + Add Link
            </button>
          }
        />
        <div className="p-[22px] flex flex-col gap-3">
          <p className="text-[12.5px] text-muted">
            The "Programmes" column in the site footer. Leave the list empty to show the built-in default.
            Link can be a page name (<code>academics</code>) or a path such as{" "}
            <code>/academics/kcgs</code> or <code>/departments/fashion-design</code>.
          </p>
          {footerProgs.length === 0 && (
            <div className="text-muted text-sm">Using the default list.</div>
          )}
          {footerProgs.map((l, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[1.4fr_2fr_auto_auto] gap-3 items-center">
              <input
                value={l.label || ""}
                onChange={(e) => setFooterProgs(footerProgs.map((x, idx) => (idx === i ? { ...x, label: e.target.value } : x)))}
                placeholder="Label, e.g. HSC – Science"
                className="w-full px-3 py-2 border-[1.5px] border-line rounded-lg font-sans text-sm outline-none focus:border-green"
              />
              <input
                value={l.href || ""}
                onChange={(e) => setFooterProgs(footerProgs.map((x, idx) => (idx === i ? { ...x, href: e.target.value } : x)))}
                placeholder="/academics/kcgs"
                className="w-full px-3 py-2 border-[1.5px] border-line rounded-lg font-sans text-sm outline-none focus:border-green"
              />
              <div className="flex gap-1">
                <button
                  onClick={() => { if (i === 0) return; const c = [...footerProgs]; [c[i - 1], c[i]] = [c[i], c[i - 1]]; setFooterProgs(c); }}
                  title="Move up"
                  className="bg-[#f2f4f6] border-none rounded-md px-2 py-1 cursor-pointer text-xs"
                >
                  ↑
                </button>
                <button
                  onClick={() => { if (i === footerProgs.length - 1) return; const c = [...footerProgs]; [c[i + 1], c[i]] = [c[i], c[i + 1]]; setFooterProgs(c); }}
                  title="Move down"
                  className="bg-[#f2f4f6] border-none rounded-md px-2 py-1 cursor-pointer text-xs"
                >
                  ↓
                </button>
              </div>
              <button
                onClick={() => setFooterProgs(footerProgs.filter((_, idx) => idx !== i))}
                title="Remove"
                className="bg-[#fdeaea] border-none rounded-md px-2.5 py-2 cursor-pointer text-xs text-[#c44]"
              >
                🗑
              </button>
            </div>
          ))}
        </div>
      </AdminCard>

      <AdminCard className="mb-[18px]">
        <AdminCardHead
          title="Social Links"
          action={
            <button
              onClick={() => setSocials([...socials, { platform: "", url: "" }])}
              className="bg-green text-white border-none rounded-lg px-3.5 py-1.5 font-sans font-semibold text-xs cursor-pointer hover:opacity-90"
            >
              + Add Link
            </button>
          }
        />
        <div className="p-[22px] flex flex-col gap-3">
          {socials.length === 0 && <div className="text-muted text-sm">No social links yet.</div>}
          {socials.map((s, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-3 items-center">
              <input
                value={s.platform || ""}
                onChange={(e) => setSocials(socials.map((x, idx) => (idx === i ? { ...x, platform: e.target.value } : x)))}
                placeholder="Platform (Facebook, YouTube…)"
                className="w-full px-3 py-2 border-[1.5px] border-line rounded-lg font-sans text-sm outline-none focus:border-green"
              />
              <input
                value={s.url || ""}
                onChange={(e) => setSocials(socials.map((x, idx) => (idx === i ? { ...x, url: e.target.value } : x)))}
                placeholder="https://…"
                className="w-full px-3 py-2 border-[1.5px] border-line rounded-lg font-sans text-sm outline-none focus:border-green"
              />
              <button
                onClick={() => setSocials(socials.filter((_, idx) => idx !== i))}
                title="Remove"
                className="bg-[#fdeaea] border-none rounded-md px-2.5 py-2 cursor-pointer text-xs text-[#c44]"
              >
                🗑
              </button>
            </div>
          ))}
        </div>
        <div className="px-[22px] py-[18px] border-t border-[#eef1f3] flex items-center gap-3">
          <button
            onClick={save}
            className="bg-green text-white border-none rounded-[9px] px-[26px] py-[11px] font-sans font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity"
          >
            Save Changes
          </button>
          {status && <span className="text-[13px] text-[#1a9f5e] font-semibold">{status}</span>}
        </div>
      </AdminCard>
        </>
      )}

      {tab === "password" && (
      <AdminCard>
        <AdminCardHead title="Change Password" />
        <div className="p-[22px] grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-[12.5px] font-semibold text-muted block mb-1.5">Current password</label>
            <input type="password" autoComplete="current-password" value={cp.current} onChange={(e) => setCp({ ...cp, current: e.target.value })} className={pwInput} />
          </div>
          <div>
            <label className="text-[12.5px] font-semibold text-muted block mb-1.5">New password</label>
            <input type="password" autoComplete="new-password" value={cp.next} onChange={(e) => setCp({ ...cp, next: e.target.value })} className={pwInput} />
          </div>
          <div>
            <label className="text-[12.5px] font-semibold text-muted block mb-1.5">Confirm new</label>
            <input type="password" autoComplete="new-password" value={cp.confirm} onChange={(e) => setCp({ ...cp, confirm: e.target.value })} className={pwInput} />
          </div>
        </div>
        {cpError && <div className="px-[22px] text-[13px] text-red">{cpError}</div>}
        <div className="px-[22px] py-[18px] flex items-center gap-3">
          <button
            onClick={changePassword}
            disabled={cpBusy || !cp.current || !cp.next}
            className="bg-green text-white border-none rounded-[9px] px-[26px] py-[11px] font-sans font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            {cpBusy ? "Updating…" : "Update Password"}
          </button>
          {cpStatus && <span className="text-[13px] text-[#1a9f5e] font-semibold">{cpStatus}</span>}
        </div>
      </AdminCard>
      )}
    </div>
  );
}
